import React, { useState, useRef, useMemo } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Snackbar,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import BuildIcon from "@mui/icons-material/Build";
import PublishIcon from "@mui/icons-material/Publish";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const API_BASE = "https://figazuresql.swancreate.com/api/swa";
const SCHOOL_MST_URL = `${API_BASE}/SchoolMst`;
const INCOME_URL = `${API_BASE}/FINANCE_Incomes`;
const CATEGORIES_URL = `${API_BASE}/FINANCE_Categories`;
const SUBCATEGORIES_URL = `${API_BASE}/FINANCE_Subcategories`;

const MAIN_CATEGORY_ID = 1;
const SUBCATEGORIES = {
    STUDENT_LUNCHES: { id: 19, name: "Sale of Student Lunches" },
    STUDENT_BREAKFASTS: { id: 20, name: "Sale of Student Breakfasts" },
    ADULT_MEALS: { id: 21, name: "Sale of Adult Breakfasts/Lunches" },
    ALA_CARTE: { id: 22, name: "Student & Adult A La Carte Sales" },
    OTHER_FOOD: { id: 23, name: "Other Food Sales" },
    CATERING_MISC: { id: 24, name: "Catering, Rebates (Other Misc. Local Sources)" },
};

const clean = (s) => (s ?? "").toString().trim();
const num = (v) => {
    const s = clean(v).replace(/[, $]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
};

const parseXML = (xmlString) => {
    const parser = new DOMParser();
    return parser.parseFromString(xmlString, "text/xml");
};

const extractSchoolCode = (schoolName) => {
    const match = schoolName.match(/Site:\s*(\d+)/);
    return match ? match[1] : "";
};

const extractSchoolName = (schoolName) => {
    const match = schoolName.match(/Site:\s*\d+\s+(.+)/);
    return match ? match[1].trim() : schoolName;
};

const determineSubcategory = (mealType, itemDescription) => {
    const mealTypeLower = clean(mealType).toLowerCase();
    const itemLower = clean(itemDescription).toLowerCase();

    if (
        itemLower.includes("adult") ||
        itemLower.includes("staff") ||
        itemLower.includes("visitor")
    ) {
        return SUBCATEGORIES.ADULT_MEALS;
    }
    if (itemLower.includes("reimbursable breakfast"))
        return SUBCATEGORIES.STUDENT_BREAKFASTS;
    if (itemLower.includes("reimbursable lunch"))
        return SUBCATEGORIES.STUDENT_LUNCHES;

    if (mealTypeLower.includes("breakfast")) {
        if (
            itemLower.includes("extra") ||
            itemLower.includes("a la carte") ||
            itemLower.includes("ala carte")
        ) {
            return SUBCATEGORIES.ALA_CARTE;
        }
        return SUBCATEGORIES.STUDENT_BREAKFASTS;
    }

    if (mealTypeLower.includes("lunch")) {
        const alaCarteItems = [
            "water",
            "slushie",
            "brownie",
            "chips",
            "cookie",
            "cupcake",
            "ice cream",
            "pretzel",
            "fruit rollup",
            "fruit snacks",
            "rice krispie",
            "scooby snack",
            "envy",
            "frozen yogurt",
            "lemonade",
            "parfait",
            "pop-tart",
            "sparkling ice",
            "string cheese",
            "condiment",
            "muffin",
            "cinnamon roll",
        ];

        if (
            alaCarteItems.some((item) => itemLower.includes(item)) ||
            itemLower.includes("extra") ||
            itemLower.includes("a la carte") ||
            itemLower.includes("ala carte")
        ) {
            return SUBCATEGORIES.ALA_CARTE;
        }
        return SUBCATEGORIES.STUDENT_LUNCHES;
    }

    if (mealTypeLower.includes("supper") || mealTypeLower.includes("cacfp")) {
        return SUBCATEGORIES.OTHER_FOOD;
    }

    return SUBCATEGORIES.ALA_CARTE;
};

const getSubcategoryColor = (subcategoryId) => {
    const colorMap = {
        19: "success",
        20: "info",
        21: "warning",
        22: "secondary",
        23: "default",
        24: "primary",
    };
    return colorMap[subcategoryId] || "default";
};

const resolveSchool = (schoolCode, schoolName, schoolMst, logFunc) => {
    if (!schoolMst || schoolMst.length === 0) {
        logFunc?.("error", "School master data is empty!");
        return null;
    }

    if (schoolCode) {
        const cleanCode = clean(schoolCode).replace(/^0+/, "");

        for (const school of schoolMst) {
            const possibleCodes = [
                school.SchoolCode,
                school.SiteId,
                school.SiteID,
                school.Code,
                school.SchoolNumber,
            ].map((c) => (c ? clean(c).replace(/^0+/, "") : ""));

            if (possibleCodes.some((c) => c === cleanCode)) {
                logFunc?.(
                    "info",
                    `Matched by code: ${schoolCode} -> ${school.SchoolName || school.SiteName
                    }`
                );
                return school;
            }
        }

        logFunc?.("warn", `No code match for: ${schoolCode}`);
    }

    const normalizedName = clean(schoolName)
        .toLowerCase()
        .replace(/elementary/gi, "")
        .replace(/middle/gi, "")
        .replace(/high/gi, "")
        .replace(/school/gi, "")
        .trim();

    for (const school of schoolMst) {
        const siteName = clean(school.SchoolName || school.SiteName || "")
            .toLowerCase()
            .replace(/elementary/gi, "")
            .replace(/middle/gi, "")
            .replace(/high/gi, "")
            .replace(/school/gi, "")
            .trim();

        if (
            siteName &&
            normalizedName &&
            (siteName.includes(normalizedName) || normalizedName.includes(siteName))
        ) {
            logFunc?.(
                "info",
                `Matched by name: ${schoolName} -> ${school.SchoolName || school.SiteName
                }`
            );
            return school;
        }
    }

    logFunc?.("error", `Could not resolve: ${schoolName} (${schoolCode})`);
    return null;
};

export default function PrimeroXMLSalesImport() {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [activeStep, setActiveStep] = useState(0);
    const [fileName, setFileName] = useState("");
    const [reportDate, setReportDate] = useState("");
    const [error, setError] = useState("");

    const [loadingRef, setLoadingRef] = useState(false);
    const [parseLoading, setParseLoading] = useState(false);
    const [buildLoading, setBuildLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [parsedData, setParsedData] = useState([]);
    const [incomeRows, setIncomeRows] = useState([]);
    const [exportSummary, setExportSummary] = useState(null);

    const [schoolMst, setSchoolMst] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [infoDialog, setInfoDialog] = useState({
        open: false,
        title: "",
        content: "",
    });
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        content: "",
        action: null,
    });
    const [processingDialog, setProcessingDialog] = useState({
        open: false,
        title: "",
        message: "",
        progress: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showLog, setShowLog] = useState(false);

    const logRef = useRef([]);

    const log = (level, msg, payload = null) => {
        const entry = { ts: new Date().toISOString(), level, msg, payload };
        logRef.current.push(entry);
        console.log(`[${level.toUpperCase()}] ${msg}`, payload || "");
    };

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const showInfo = (title, content) => {
        setInfoDialog({ open: true, title, content });
    };

    const showConfirm = (title, content, action) => {
        setConfirmDialog({ open: true, title, content, action });
    };

    const showProcessing = (title, message, progress = 0) => {
        setProcessingDialog({ open: true, title, message, progress });
    };

    const hideProcessing = () => {
        setProcessingDialog({ open: false, title: "", message: "", progress: 0 });
    };

    React.useEffect(() => {
        loadReferenceData();
    }, []);

    const loadReferenceData = async () => {
        setLoadingRef(true);
        showProcessing("Loading Data", "Connecting to database...", 30);

        try {
            log("info", "Loading reference data...");

            const [schoolsRes, catsRes, subcatsRes] = await Promise.all([
                axios.get(SCHOOL_MST_URL),
                axios.get(CATEGORIES_URL),
                axios.get(SUBCATEGORIES_URL),
            ]);

            setSchoolMst(schoolsRes.data || []);
            setCategories(catsRes.data || []);
            setSubcategories(subcatsRes.data || []);

            log("info", `Loaded ${schoolsRes.data?.length || 0} schools`);

            if (schoolsRes.data && schoolsRes.data.length > 0) {
                log("info", "Schools:", {
                    schools: schoolsRes.data.map((s) => ({
                        code: s.SchoolCode || s.SiteId || "NO_CODE",
                        name: s.SchoolName || s.SiteName || "NO_NAME",
                    })),
                });
            }

            showSnackbar(
                `Loaded ${schoolsRes.data?.length || 0} schools`,
                "success"
            );
        } catch (err) {
            log("error", "Failed to load data", err.message);
            setError("Failed to load: " + err.message);
            showSnackbar("Failed to load data", "error");
        } finally {
            setLoadingRef(false);
            hideProcessing();
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!reportDate) {
            setError("Please select a report date first!");
            showSnackbar("Please select a report date before uploading", "error");
            event.target.value = "";
            return;
        }

        showInfo(
            "Processing XML",
            `Parsing ${file.name} for ${reportDate}...\n\n• Reading XML\n• Extracting sales\n• Filtering $0 items\n• Auto-categorizing\n• Aggregating by school`
        );

        setFileName(file.name);
        setError("");
        setParsedData([]);
        setIncomeRows([]);
        setExportSummary(null);
        setParseLoading(true);
        setActiveStep(1);

        showProcessing("Parsing XML", "Reading file...", 20);

        try {
            log("info", `Reading XML for date: ${reportDate}`, file.name);

            const text = await file.text();

            showProcessing("Parsing XML", "Analyzing structure...", 40);

            const xmlDoc = parseXML(text);

            const parseError = xmlDoc.querySelector("parsererror");
            if (parseError) {
                throw new Error("Invalid XML format");
            }

            log("info", "Extracting sales...");
            showProcessing("Parsing XML", "Extracting sales...", 60);

            const schoolGroups = xmlDoc.querySelectorAll("table1_Group3");
            const salesData = [];
            let itemCount = 0;

            schoolGroups.forEach((schoolGroup) => {
                const schoolNameAttr = schoolGroup.getAttribute("School2");
                if (!schoolNameAttr) return;

                const schoolCode = extractSchoolCode(schoolNameAttr);
                const schoolName = extractSchoolName(schoolNameAttr);

                log("info", `Processing: ${schoolName} (${schoolCode})`);

                const mealTypeGroups = schoolGroup.querySelectorAll("table1_Group4");

                mealTypeGroups.forEach((mealGroup) => {
                    const mealType = mealGroup.getAttribute("MealType3") || "";
                    const details = mealGroup.querySelectorAll("Detail");

                    details.forEach((detail) => {
                        const itemDescription =
                            detail.getAttribute("MenuItemDescription2") || "";
                        const menuItemPrice = num(
                            detail.getAttribute("MenuItemPrice2")
                        );

                        if (menuItemPrice === 0) return;

                        const cashSale = num(detail.getAttribute("CashSale2"));
                        const debitSale = num(detail.getAttribute("DebitSale2"));
                        const totalAmount = cashSale + debitSale;

                        if (totalAmount > 0) {
                            salesData.push({
                                schoolCode,
                                schoolName,
                                mealType,
                                itemDescription,
                                cashSale,
                                debitSale,
                                totalAmount,
                            });
                            itemCount++;
                        }
                    });
                });
            });

            showProcessing("Parsing XML", "Aggregating...", 80);

            log("info", `Parsed ${itemCount} items from ${schoolGroups.length} schools`);

            const aggregated = {};

            salesData.forEach((item) => {
                const subcategory = determineSubcategory(
                    item.mealType,
                    item.itemDescription
                );
                const key = `${item.schoolCode}_${subcategory.id}`;

                if (!aggregated[key]) {
                    aggregated[key] = {
                        schoolCode: item.schoolCode,
                        schoolName: item.schoolName,
                        subcategory: subcategory,
                        totalAmount: 0,
                        items: [],
                    };
                }

                aggregated[key].totalAmount += item.totalAmount;
                aggregated[key].items.push(item.itemDescription);
            });

            const aggregatedArray = Object.values(aggregated);

            setParsedData(aggregatedArray);
            setActiveStep(2);

            log("info", `Aggregated into ${aggregatedArray.length} entries for ${reportDate}`);

            showSnackbar(
                `Parsed ${itemCount} items into ${aggregatedArray.length} entries`,
                "success"
            );
        } catch (err) {
            log("error", "Parse failed", err.message);
            setError("Parse failed: " + err.message);
            showSnackbar("Parse failed: " + err.message, "error");
            setActiveStep(0);
        } finally {
            setParseLoading(false);
            hideProcessing();
        }
    };

    const handleBuildIncomeRows = () => {
        showConfirm(
            "Build Income Rows",
            `Create ${parsedData.length} income records for ${reportDate}?\n\n• Match schools\n• Assign categories\n• Create descriptions\n• Prepare for export`,
            buildIncomeRows
        );
    };

    const buildIncomeRows = async () => {
        if (parsedData.length === 0) {
            showSnackbar("No data to build", "warning");
            return;
        }

        setBuildLoading(true);
        showProcessing("Building Rows", "Matching schools...", 30);

        log("info", `Building income rows for ${reportDate}...`);

        try {
            const rows = [];
            const monthDate = reportDate;
            let processed = 0;
            const total = parsedData.length;
            let unmatchedSchools = [];

            const mainCategory = categories.find(
                (c) =>
                    String(c.CategoryID) === String(MAIN_CATEGORY_ID) ||
                    String(c.CategoryId) === String(MAIN_CATEGORY_ID)
            );

            for (const item of parsedData) {
                processed++;
                showProcessing(
                    "Building Rows",
                    `Processing ${processed} of ${total}...`,
                    30 + (processed / total) * 60
                );

                log(
                    "info",
                    `Resolving: ${item.schoolName} (${item.schoolCode})`
                );
                const school = resolveSchool(
                    item.schoolCode,
                    item.schoolName,
                    schoolMst,
                    log
                );

                if (!school) {
                    log(
                        "warn",
                        `Failed: ${item.schoolName} (${item.schoolCode})`
                    );
                    unmatchedSchools.push(
                        `${item.schoolName} (${item.schoolCode})`
                    );
                    continue;
                }

                const schoolId =
                    school.SchoolId ||
                    school.SiteID ||
                    school.SiteId ||
                    school.apiid ||
                    null;
                const districtId =
                    school.SchoolDistrictId ||
                    school.SchoolDistrictID ||
                    school.DistrictID ||
                    null;
                const districtName = school.DistrictName || "Flagler Schools";

                const subcat = item.subcategory;
                const amount = Number(item.totalAmount.toFixed(2));

                // LOOKUP KPI FIELDS FROM SUBCATEGORIES TABLE
                const subcategoryRecord = subcategories.find(
                    (s) => String(s.SubcategoryID) === String(subcat.id)
                );
                const kpiid = subcategoryRecord?.kpiid || null;
                const kpi_categoryname = subcategoryRecord?.kpi_categoryname || null;

                const incomeRow = {
                    IncomeDate: reportDate,
                    Amount: amount,
                    IncomeCategoryID: String(MAIN_CATEGORY_ID),
                    IncomeSubCategoryID: String(subcat.id),
                    Description: `${subcat.name} - ${item.schoolName}`,
                    isActive: true,
                    SchoolDistrictId: districtId,
                    SchoolId: schoolId,
                    SiteName:
                        school.SchoolName ||
                        school.SiteName ||
                        item.schoolName,
                    IncomeCategory:
                        mainCategory?.CategoryName || "CASH-RECEIPTS",
                    IncomeSubcategory: subcat.name,
                    DistrictName: districtName,
                    ProcessID: "PrimeroXML",
                    MonthDate: monthDate,
                    catFlag: true,
                    kpiid: kpiid,
                    kpi_categoryname: kpi_categoryname,
                    CreatedBy: "PrimeroImport",
                    CreatedOn: new Date().toISOString(),
                    UpdatedBy: null,
                    UpdatedOn: null,
                };

                rows.push(incomeRow);
                log("info", "Built income row", incomeRow);
            }

            setIncomeRows(rows);
            setActiveStep(3);

            log("info", `Built ${rows.length} rows for ${reportDate}`);

            if (unmatchedSchools.length > 0) {
                showSnackbar(
                    `Built ${rows.length} rows. ${unmatchedSchools.length} schools unmatched`,
                    "warning"
                );
                showInfo(
                    "Unmatched Schools",
                    `Could not match:\n\n${unmatchedSchools.join(
                        "\n"
                    )}\n\nCheck school codes in database`
                );
            } else {
                showSnackbar(`Built ${rows.length} rows`, "success");
            }
        } catch (err) {
            log("error", "Build failed", err.message);
            setError("Build failed: " + err.message);
            showSnackbar("Build failed: " + err.message, "error");
        } finally {
            setBuildLoading(false);
            hideProcessing();
        }
    };

    const handleExport = () => {
        const totalAmount = incomeRows.reduce(
            (sum, row) => sum + (row.Amount || 0),
            0
        );

        showConfirm(
            "Export to Database",
            `Export ${incomeRows.length} records for ${reportDate} (${totalAmount.toLocaleString(
                "en-US",
                { style: "currency", currency: "USD" }
            )})?\n\n• POST new records\n• Cannot be undone`,
            exportToAPI
        );
    };

    const exportToAPI = async () => {
        if (incomeRows.length === 0) {
            showSnackbar("No rows to export", "warning");
            return;
        }

        setExportLoading(true);
        setExportSummary(null);
        showProcessing("Exporting", "Starting...", 10);

        log("info", `Exporting to FINANCE_Incomes for ${reportDate}...`);

        const summary = {
            incInserted: 0,
            incUpdated: 0,
            failures: [],
        };

        try {
            const total = incomeRows.length;
            let processed = 0;

            for (const row of incomeRows) {
                processed++;
                showProcessing(
                    "Exporting",
                    `Record ${processed} of ${total}: ${row.SiteName}...`,
                    10 + (processed / total) * 80
                );

                try {
                    const { apiid, IncomeID, ...payload } = row;

                    log("info", "POST payload to FINANCE_Incomes", payload);

                    const res = await axios.post(INCOME_URL, payload);

                    summary.incInserted++;
                    log(
                        "info",
                        `Inserted income for ${row.SiteName} / ${row.IncomeSubcategory} on ${row.IncomeDate}`,
                        res.data
                    );
                } catch (err) {
                    const msg = `${row.SiteName} - ${row.IncomeSubcategory
                        }: ${err.message || err}`;
                    summary.failures.push(msg);
                    log("error", msg, err);
                }
            }

            setExportSummary(summary);
            setActiveStep(4);

            log("info", "Export complete", summary);

            const successMsg = `Complete! Inserted: ${summary.incInserted}${summary.failures.length > 0
                ? `, Failures: ${summary.failures.length}`
                : ""
                }`;
            showSnackbar(
                successMsg,
                summary.failures.length > 0 ? "warning" : "success"
            );

            if (summary.failures.length === 0) {
                showInfo(
                    "Success!",
                    `All ${incomeRows.length} records exported for ${reportDate}\n\n• ${summary.incInserted} inserted`
                );
            }
        } catch (err) {
            log("error", "Export failed", err.message);
            setError("Export failed: " + err.message);
            showSnackbar("Export failed: " + err.message, "error");
        } finally {
            setExportLoading(false);
            hideProcessing();
        }
    };

    const handleReset = () => {
        showConfirm(
            "Start New Import",
            "Clear all data and start over?\n\nUnsaved data will be lost.",
            () => {
                setActiveStep(0);
                setFileName("");
                setReportDate("");
                setError("");
                setParsedData([]);
                setIncomeRows([]);
                setExportSummary(null);
                logRef.current = [];
                showSnackbar("Ready for new import", "info");
            }
        );
    };

    const previewColumns = useMemo(
        () => [
            {
                field: "schoolName",
                headerName: "School",
                flex: 2,
                minWidth: 200,
                renderCell: (params) => (
                    <Tooltip title={`Code: ${params.row.schoolCode}`}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <SchoolIcon fontSize="small" color="action" />
                            <span>{params.value}</span>
                        </Box>
                    </Tooltip>
                ),
            },
            {
                field: "schoolCode",
                headerName: "Code",
                width: 100,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace" }}
                    />
                ),
            },
            {
                field: "subcategoryName",
                headerName: "Subcategory",
                flex: 1.5,
                minWidth: 200,
                valueGetter: (params) => params.row.subcategory?.name || "",
                renderCell: (params) => {
                    const subcatId = params.row.subcategory?.id;
                    const color = getSubcategoryColor(subcatId);
                    return (
                        <Chip
                            label={params.value}
                            size="small"
                            color={color}
                            icon={<RestaurantIcon />}
                            sx={{ fontWeight: 600 }}
                        />
                    );
                },
            },
            {
                field: "totalAmount",
                headerName: "Total Amount",
                flex: 1,
                minWidth: 150,
                type: "number",
                valueFormatter: ({ value }) =>
                    (Number(value) || 0).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }),
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontWeight: 700,
                            color: theme.palette.success.main,
                        }}
                    >
                        <AttachMoneyIcon fontSize="small" />
                        {params.formattedValue}
                    </Box>
                ),
            },
            {
                field: "itemCount",
                headerName: "Items",
                width: 100,
                type: "number",
                valueGetter: (params) => params.row.items?.length || 0,
                renderCell: (params) => (
                    <Tooltip
                        title={
                            (params.row.items?.slice(0, 5).join(", ") || "") +
                            (params.row.items?.length > 5 ? "..." : "")
                        }
                    >
                        <Chip
                            label={params.value}
                            size="small"
                            color="info"
                            sx={{ fontWeight: 600 }}
                        />
                    </Tooltip>
                ),
            },
        ],
        [theme]
    );

    const steps = [
        {
            label: "Upload XML",
            description: "Select report date and Menu Item Sales By Item Report XML",
            icon: <CloudUploadIcon />,
        },
        {
            label: "Parse Data",
            description: "Extract and filter sales data",
            icon: <InfoIcon />,
        },
        {
            label: "Review",
            description: "Verify aggregated data",
            icon: <CheckCircleIcon />,
        },
        {
            label: "Build Rows",
            description: "Create income records",
            icon: <BuildIcon />,
        },
        {
            label: "Export",
            description: "Save to database",
            icon: <PublishIcon />,
        },
    ];

    return (
        <Box p={3} display="flex" flexDirection="column" gap={3} width="100%">
            <Dialog
                open={infoDialog.open}
                onClose={() =>
                    setInfoDialog({ open: false, title: "", content: "" })
                }
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                    }}
                >
                    <InfoIcon color="info" />
                    {infoDialog.title}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText
                        sx={{
                            whiteSpace: "pre-line",
                            color: theme.palette.text.primary,
                        }}
                    >
                        {infoDialog.content}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setInfoDialog({
                                open: false,
                                title: "",
                                content: "",
                            })
                        }
                        variant="contained"
                    >
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({
                        open: false,
                        title: "",
                        content: "",
                        action: null,
                    })
                }
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }}
                >
                    <WarningIcon color="warning" />
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText
                        sx={{
                            whiteSpace: "pre-line",
                            color: theme.palette.text.primary,
                        }}
                    >
                        {confirmDialog.content}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setConfirmDialog({
                                open: false,
                                title: "",
                                content: "",
                                action: null,
                            })
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            confirmDialog.action?.();
                            setConfirmDialog({
                                open: false,
                                title: "",
                                content: "",
                                action: null,
                            });
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={processingDialog.open}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
            >
                <DialogTitle
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                >
                    {processingDialog.title}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            {processingDialog.message}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={processingDialog.progress}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                ),
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 5,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            textAlign="center"
                            fontWeight={600}
                        >
                            {Math.round(processingDialog.progress)}% Complete
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() =>
                    setSnackbar({ ...snackbar, open: false })
                }
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar({ ...snackbar, open: false })
                    }
                    severity={snackbar.severity}
                    variant="filled"
                    elevation={6}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Card
                elevation={0}
                sx={{
                    background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.9
                    )} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
                    color: "#fff",
                    border: `1px solid ${alpha(
                        theme.palette.divider,
                        0.1
                    )}`,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isDark
                            ? "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.02) 0%, transparent 50%)"
                            : "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                        pointerEvents: "none",
                    },
                }}
            >
                <CardContent sx={{ position: "relative", zIndex: 1 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                gutterBottom
                                sx={{
                                    textShadow: isDark
                                        ? "0 2px 10px rgba(0,0,0,0.3)"
                                        : "none",
                                }}
                            >
                                🍽️ Primero Sales Import
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.95 }}>
                                Import XML sales reports to FINANCE_Incomes
                            </Typography>
                        </Box>
                        <Tooltip title="Help">
                            <IconButton
                                sx={{ color: "#fff" }}
                                onClick={() =>
                                    showInfo(
                                        "Quick Guide",
                                        "1. Select report date\n2. Upload XML file\n3. Review parsed data\n4. Build income rows\n5. Export to database\n\nAuto-categorizes items and matches schools"
                                    )
                                }
                            >
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError("")}
                    variant="filled"
                    icon={<WarningIcon />}
                    sx={{
                        borderRadius: 2,
                        boxShadow: `0 4px 20px ${alpha(
                            theme.palette.error.main,
                            0.3
                        )}`,
                    }}
                >
                    {error}
                </Alert>
            )}

            <Card elevation={2} sx={{ bgcolor: theme.palette.background.paper }}>
                <CardContent>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {steps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    icon={step.icon}
                                    StepIconProps={{
                                        sx: {
                                            "&.Mui-active": {
                                                color: theme.palette.primary.main,
                                                filter:
                                                    "drop-shadow(0 0 8px currentColor)",
                                            },
                                            "&.Mui-completed": {
                                                color: theme.palette.success.main,
                                            },
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                    >
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        paragraph
                                    >
                                        {step.description}
                                    </Typography>

                                    {index === 0 && (
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <TextField
                                                label="Report Date"
                                                type="date"
                                                value={reportDate}
                                                onChange={(e) => setReportDate(e.target.value)}
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'action.active' }} />
                                                }}
                                                helperText="Select the month/date for this sales report"
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '&:hover fieldset': {
                                                            borderColor: theme.palette.primary.main,
                                                            borderWidth: 2
                                                        },
                                                        '&.Mui-focused fieldset': { borderWidth: 2 }
                                                    }
                                                }}
                                            />
                                            <Box>
                                                <input
                                                    id="xml-file"
                                                    type="file"
                                                    accept=".xml"
                                                    hidden
                                                    onChange={handleFileUpload}
                                                    disabled={!reportDate}
                                                />
                                                <label htmlFor="xml-file">
                                                    <Button
                                                        variant="contained"
                                                        component="span"
                                                        startIcon={<CloudUploadIcon />}
                                                        size="large"
                                                        disabled={
                                                            parseLoading || loadingRef || !reportDate
                                                        }
                                                        sx={{
                                                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                            boxShadow: `0 3px 15px ${alpha(
                                                                theme.palette.primary.main,
                                                                0.4
                                                            )}`,
                                                            "&:hover": {
                                                                boxShadow: `0 6px 20px ${alpha(
                                                                    theme.palette
                                                                        .primary.main,
                                                                    0.6
                                                                )}`,
                                                            },
                                                        }}
                                                    >
                                                        {fileName || "Choose XML File"}
                                                    </Button>
                                                </label>
                                                {fileName && (
                                                    <Chip
                                                        label={`Selected: ${fileName}`}
                                                        color="success"
                                                        sx={{ ml: 2 }}
                                                        onDelete={() => {
                                                            setFileName("");
                                                            const input =
                                                                document.getElementById(
                                                                    "xml-file"
                                                                );
                                                            if (input) input.value = "";
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {!reportDate && (
                                                <Alert severity="warning" icon={<CalendarTodayIcon />}>
                                                    Please select a report date before uploading
                                                </Alert>
                                            )}
                                        </Box>
                                    )}

                                    {index === 2 && parsedData.length > 0 && (
                                        <Box>
                                            <Alert
                                                severity="success"
                                                sx={{ mb: 2 }}
                                                variant="outlined"
                                            >
                                                ✓ Parsed {parsedData.length} entries for {reportDate} from{" "}
                                                {
                                                    new Set(
                                                        parsedData.map(
                                                            (d) => d.schoolCode
                                                        )
                                                    ).size
                                                }{" "}
                                                schools
                                            </Alert>
                                            <Button
                                                variant="contained"
                                                onClick={handleBuildIncomeRows}
                                                startIcon={<BuildIcon />}
                                                disabled={buildLoading}
                                                size="large"
                                                sx={{
                                                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                                    boxShadow: `0 3px 15px ${alpha(
                                                        theme.palette.secondary.main,
                                                        0.4
                                                    )}`,
                                                }}
                                            >
                                                {buildLoading ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    "Build Income Rows"
                                                )}
                                            </Button>
                                        </Box>
                                    )}

                                    {index === 3 && incomeRows.length > 0 && (
                                        <Box>
                                            <Alert
                                                severity="info"
                                                sx={{ mb: 2 }}
                                                variant="outlined"
                                            >
                                                ✓ {incomeRows.length} records ready for {reportDate} •
                                                Total:{" "}
                                                {incomeRows
                                                    .reduce(
                                                        (sum, r) =>
                                                            sum +
                                                            (r.Amount || 0),
                                                        0
                                                    )
                                                    .toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                    })}
                                            </Alert>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                onClick={handleExport}
                                                startIcon={<PublishIcon />}
                                                disabled={exportLoading}
                                                size="large"
                                                sx={{
                                                    boxShadow: `0 3px 15px ${alpha(
                                                        theme.palette.success.main,
                                                        0.4
                                                    )}`,
                                                    "&:hover": {
                                                        boxShadow: `0 6px 20px ${alpha(
                                                            theme.palette.success.main,
                                                            0.6
                                                        )}`,
                                                    },
                                                }}
                                            >
                                                {exportLoading ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    "Export to FINANCE_Incomes"
                                                )}
                                            </Button>
                                        </Box>
                                    )}

                                    {index === 4 && exportSummary && (
                                        <Box>
                                            <Alert
                                                severity={
                                                    exportSummary.failures?.length >
                                                        0
                                                        ? "warning"
                                                        : "success"
                                                }
                                                sx={{ mb: 2 }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    gutterBottom
                                                    fontWeight={600}
                                                >
                                                    Export Summary for {reportDate}
                                                </Typography>
                                                <Typography variant="body2">
                                                    ✓ Inserted:{" "}
                                                    {exportSummary.incInserted}
                                                    <br />
                                                    ✓ Updated:{" "}
                                                    {exportSummary.incUpdated}
                                                    <br />
                                                    {exportSummary.failures
                                                        ?.length > 0 &&
                                                        `✗ Failures: ${exportSummary.failures.length}`}
                                                </Typography>
                                            </Alert>
                                            <Button
                                                variant="outlined"
                                                onClick={handleReset}
                                                startIcon={<RefreshIcon />}
                                                sx={{
                                                    borderWidth: 2,
                                                    "&:hover": { borderWidth: 2 },
                                                }}
                                            >
                                                Start New Import
                                            </Button>
                                        </Box>
                                    )}
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                </CardContent>
            </Card>

            {parsedData.length > 0 && activeStep >= 2 && (
                <Card
                    elevation={3}
                    sx={{ bgcolor: theme.palette.background.paper }}
                >
                    <CardContent>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                        >
                            <Typography
                                variant="h6"
                                fontWeight={600}
                            >
                                📊 Parsed Sales Data ({parsedData.length}) - {reportDate}
                            </Typography>
                            <Chip
                                label={`Total: ${parsedData
                                    .reduce(
                                        (sum, d) => sum + d.totalAmount,
                                        0
                                    )
                                    .toLocaleString("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                    })}`}
                                color="success"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    px: 1,
                                    boxShadow: `0 2px 8px ${alpha(
                                        theme.palette.success.main,
                                        0.3
                                    )}`,
                                }}
                                icon={<AttachMoneyIcon />}
                            />
                        </Box>
                        <Box
                            sx={{
                                height: 500,
                                width: "100%",
                                "& .MuiDataGrid-root": {
                                    border: "none",
                                    bgcolor: theme.palette.background.default,
                                },
                                "& .MuiDataGrid-cell:focus": {
                                    outline: "none",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.05
                                    ),
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                    ),
                                    borderRadius: "8px 8px 0 0",
                                },
                            }}
                        >
                            <DataGrid
                                rows={parsedData.map((item, idx) => ({
                                    id: idx,
                                    ...item,
                                }))}
                                columns={previewColumns}
                                pageSize={25}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                disableSelectionOnClick
                                density="comfortable"
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {incomeRows.length > 0 && activeStep >= 3 && (
                <Card
                    elevation={3}
                    sx={{ bgcolor: theme.palette.background.paper }}
                >
                    <CardContent>
                        <Typography
                            variant="h6"
                            gutterBottom
                            fontWeight={600}
                        >
                            💰 Income Records ({incomeRows.length}) - {reportDate}
                        </Typography>
                        <Box sx={{ overflowX: "auto", maxHeight: 600 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            Date
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            School
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            Category
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            Subcategory
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            Amount
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1
                                                ),
                                                fontWeight: 700,
                                            }}
                                        >
                                            Description
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {incomeRows.map((row, i) => (
                                        <TableRow
                                            key={i}
                                            hover
                                            sx={{
                                                "&:hover": {
                                                    bgcolor: alpha(
                                                        theme.palette.primary.main,
                                                        0.03
                                                    ),
                                                },
                                            }}
                                        >
                                            <TableCell>{row.IncomeDate}</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>
                                                {row.SiteName}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.IncomeCategory}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        row.IncomeSubcategory
                                                    }
                                                    size="small"
                                                    color={getSubcategoryColor(
                                                        parseInt(
                                                            row.IncomeSubCategoryID
                                                        )
                                                    )}
                                                    icon={<RestaurantIcon />}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: theme.palette.success
                                                        .main,
                                                }}
                                            >
                                                {Number(
                                                    row.Amount || 0
                                                ).toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: "USD",
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    title={row.Description}
                                                >
                                                    <span>
                                                        {row.Description.substring(
                                                            0,
                                                            50
                                                        )}
                                                        {row.Description
                                                            .length > 50
                                                            ? "..."
                                                            : ""}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Card elevation={1} sx={{ bgcolor: theme.palette.background.paper }}>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            📝 Processing Log
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setShowLog(!showLog)}
                            endIcon={
                                showLog ? <CloseIcon /> : <InfoIcon />
                            }
                            variant={showLog ? "contained" : "outlined"}
                        >
                            {showLog ? "Hide" : "Show"}
                        </Button>
                    </Box>
                    {showLog && (
                        <Box
                            component="pre"
                            sx={{
                                fontSize: 11,
                                whiteSpace: "pre-wrap",
                                m: 0,
                                maxHeight: 400,
                                overflowY: "auto",
                                bgcolor: isDark
                                    ? alpha("#000", 0.3)
                                    : alpha("#000", 0.03),
                                color: theme.palette.text.primary,
                                p: 2,
                                borderRadius: 2,
                                fontFamily: "Monaco, Consolas, monospace",
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {logRef.current.map((l, i) => {
                                const levelColors = {
                                    info: theme.palette.info.main,
                                    warn: theme.palette.warning.main,
                                    error: theme.palette.error.main,
                                };
                                return (
                                    <Box
                                        key={i}
                                        component="div"
                                        sx={{
                                            mb: 0.5,
                                            color:
                                                levelColors[l.level] ||
                                                theme.palette.text.primary,
                                        }}
                                    >
                                        [
                                        {l.ts
                                            .split("T")[1]
                                            ?.split(".")[0] || ""}
                                        ] {String(l.level || "info").toUpperCase()}{" "}
                                        - {l.msg}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}